import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, DEFAULT_LOCATION, PLACE_TYPE_CATEGORIES } from '../constants/placeTypes';
import WalkingMap from './WalkingMap';
import PlaceTypeSelector from './PlaceTypeSelector';

const libraries: ("places" | "geometry")[] = ['places', 'geometry'];

interface Location {
  lat: number;
  lng: number;
}

interface Place {
  name: string;
  types?: string[];
  walkingDistanceMeters?: number;
}

interface TypeCounts {
  [key: string]: string;
}

declare global {
  interface Window {
    walkingMapRef: {
      searchPlaces?: () => void;
    };
  }
}

const WalkingDistanceCalculator: React.FC = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const [isPlaceMarkerMode, setIsPlaceMarkerMode] = useState<boolean>(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [originMarker, setOriginMarker] = useState<Location | null>(null);
  const [markerAddress] = useState<string>('Ingen position vald');
  const [status, setStatus] = useState<string>('Väntar på att kartan skall laddas...');
  const [typeCounts, setTypeCounts] = useState<TypeCounts>({});
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const walkingMapRef = useRef<any>(null);

  const handleMarkerPlaced = (location: Location) => {
    setOriginMarker(location);
    setIsPlaceMarkerMode(false);
    setStatus('Starting point placed. You can now search for places nearby.');
  };

  const handleMapLoad = (map: any, placesService: any, directionsService: any) => {
    setStatus('Map initialized at Lilla Essingen, Stockholm. Click "Sätt markör mode" to set your starting point.');
  };

  const togglePlaceMarkerMode = () => {
    setIsPlaceMarkerMode(!isPlaceMarkerMode);
    if (!isPlaceMarkerMode) {
      setStatus('Click anywhere on the map to place your starting point');
    } else {
      setStatus('Place marker mode disabled');
    }
  };

  const handleSearch = () => {
    if (!originMarker) {
      setStatus('Please place a starting point first');
      return;
    }

    if (selectedTypes.length === 0) {
      setStatus('Please select at least one place type');
      return;
    }

    setIsSearching(true);
    setStatus(`Searching for ${selectedTypes.length} type(s) within walking distance...`);

    // Trigger search in WalkingMap component
    if (window.walkingMapRef && window.walkingMapRef.searchPlaces) {
      window.walkingMapRef.searchPlaces();
    }
  };

  const handleSearchComplete = (places: Place[]) => {
    setIsSearching(false);

    // Calculate type counts
    const counts: { [key: string]: number } = {};
    const closestPlaces: { [key: string]: Place | null } = {};

    // Get display names lookup
    const displayNames: { [key: string]: string } = {};
    PLACE_TYPE_CATEGORIES.forEach(category => {
      category.options.forEach(option => {
        displayNames[option.value] = option.label;
      });
    });

    // Count places by type
    selectedTypes.forEach(type => {
      counts[type] = 0;
      closestPlaces[type] = null;
    });

    places.forEach(place => {
      if (!place.types) return;

      selectedTypes.forEach(searchedType => {
        let isMatch = false;

        // Check if place matches this type
        if (place.types && place.types.includes(searchedType)) {
          isMatch = true;
        }

        // Special handling for Swedish types
        if (['preschool', 'primary_school', 'secondary_school', 'systembolaget'].includes(searchedType)) {
          const placeName = place.name.toLowerCase();
          if (searchedType === 'preschool' && (placeName.includes('förskola') || placeName.includes('dagis'))) {
            isMatch = true;
          } else if (searchedType === 'primary_school' && placeName.includes('grundskola')) {
            isMatch = true;
          } else if (searchedType === 'secondary_school' && (placeName.includes('gymnasie') || placeName.includes('gymnasium'))) {
            isMatch = true;
          } else if (searchedType === 'systembolaget' && placeName.includes('systembolaget')) {
            isMatch = true;
          }
        }

        if (isMatch) {
          counts[searchedType]++;

          if (place.walkingDistanceMeters !== undefined) {
            if (!closestPlaces[searchedType] ||
                (closestPlaces[searchedType]?.walkingDistanceMeters &&
                 place.walkingDistanceMeters < closestPlaces[searchedType]!.walkingDistanceMeters!)) {
              closestPlaces[searchedType] = place;
            }
          }
        }
      });
    });

    // Format counts for display
    const formattedCounts: TypeCounts = {};
    Object.keys(counts).forEach(type => {
      if (counts[type] > 0) {
        let displayText = `${displayNames[type] || type}: ${counts[type]}`;

        if (closestPlaces[type] && closestPlaces[type].walkingDistanceMeters) {
          displayText += ` (Närmaste: ${closestPlaces[type].walkingDistanceMeters} m`;
          if (closestPlaces[type].name) {
            displayText += ` - ${closestPlaces[type].name}`;
          }
          displayText += ')';
        }

        formattedCounts[type] = displayText;
      }
    });

    setTypeCounts(formattedCounts);
    setStatus(`Found ${places.length} places within 10 minutes walking time.`);
  };

  if (loadError) {
    return <div className="error">Error loading maps: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div className="status">Laddar karta...</div>;
  }

  return (
    <div className="walking-calculator">
      <Link to="/" className="back-link">← Tillbaka till startsidan</Link>
      <div className="page-header">
        <img src="/Maestro Basic Logo.png" alt="Maestro Logo" />
        <h2>Vad finns på promenadavstånd?</h2>
      </div>

      <div className="api-info">
        Detta verktyg använder Google för att beräkna exakta gångavstånd och restider samt Geoapify för 10-minuters gång-isochroner. Lars Albinsson, 2025
      </div>

      <div className="map-container">
        <WalkingMap
          ref={walkingMapRef}
          center={DEFAULT_LOCATION}
          onMarkerPlaced={handleMarkerPlaced}
          isPlaceMarkerMode={isPlaceMarkerMode}
          onMapLoad={handleMapLoad}
          selectedTypes={selectedTypes}
          onSearchComplete={handleSearchComplete}
        />
      </div>

      <div className="options-container">
        <PlaceTypeSelector
          selectedTypes={selectedTypes}
          onChange={setSelectedTypes}
        />

        <div className="buttons-container">
          <div className="marker-controls">
            <button
              id="placeMarkerButton"
              className={isPlaceMarkerMode ? 'active-mode' : ''}
              onClick={togglePlaceMarkerMode}
            >
              Sätt markör mode
            </button>
            {isPlaceMarkerMode && (
              <span id="markerInstructions" style={{ marginLeft: '10px', color: '#4285f4' }}>
                <br />Klicka var som helst på kartan för att placera en markör.
              </span>
            )}
            {originMarker && (
              <div id="markerInfo" style={{ marginTop: '10px' }}>
                <strong>Adress:</strong> <span id="markerPosition">{markerAddress}</span>
              </div>
            )}
          </div>

          <button
            id="searchButton"
            onClick={handleSearch}
            disabled={!originMarker || isSearching}
          >
            {isSearching ? 'Söker...' : 'Sök platser/service inom 10 min promenad'}
          </button>

          {Object.keys(typeCounts).length > 0 && (
            <div id="type-counts">
              <strong>Platser inom 10 min promenad:</strong><br />
              {Object.values(typeCounts).map((text, index) => (
                <div key={index}>{text}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div id="status" className={status.includes('Error') || status.includes('Please') ? 'error' : 'success'}>
        {status}
      </div>

      {isSearching && (
        <div id="loading-popup">
          <div className="spinner"></div>
          <p>Söker...</p>
        </div>
      )}
    </div>
  );
};

// Set up global ref for WalkingMap to call search
window.walkingMapRef = {};

export default WalkingDistanceCalculator;
